import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { IHoliday, IPatient } from '../../../../models/patient.model';
import { IForm, IFormData } from '../../../../models/form.model';
import { ISibasiHTTPResponse } from '../../../../models/sibasiHTTPResponse.model';
import { IVisit } from '../../../../models/visit.model';
import { IAppointment } from '../../../../models/appointment.model';
import { IAllergy } from '../../../../models/allergy.model';

@Injectable({
  providedIn: 'root'
})
export class PatientService {

  constructor(
    private httpClient: HttpClient
  ) { }

  getAllPatients(): Observable<ISibasiHTTPResponse<IPatient[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IPatient[]>>(`${environment.api_url}/patient`);
  }

  createPatient(patient: IPatient): Observable<ISibasiHTTPResponse<IPatient>> {
    return this.httpClient.post<ISibasiHTTPResponse<IPatient>>(`${environment.api_url}/patient`, patient);
  }

  updatePatient(patient: Partial<IPatient>, patientId: string): Observable<ISibasiHTTPResponse<IPatient>> {
    return this.httpClient.post<ISibasiHTTPResponse<IPatient>>(`${environment.api_url}/patient/${patientId}`, patient);
  }

  submitPatientEMortalityForm(patientEMortalityForm: any, patientId: string): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.post<ISibasiHTTPResponse<any>>(`${environment.api_url}/e_mortality`, patientEMortalityForm);
  }

  getPatientEMortalityDetails(patientId: string): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.get<ISibasiHTTPResponse<any>>(`${environment.api_url}/e_mortality/patient/${patientId}`,);
  }

  // deletePatient(patientId: string): Observable<ISibasiHTTPResponse<IPatient>> {
  //   return this.httpClient.delete<ISibasiHTTPResponse<IPatient>>(`${environment.api_url}/patient/${patientId}`);
  // }

  deletePatient(patientId: string): Observable<ISibasiHTTPResponse<IPatient>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IPatient>>(`${environment.api_url}/patient/slateforDeletion/${patientId}`);
  }

  restoreDeletedPatient(patientId: string): Observable<ISibasiHTTPResponse<IPatient>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IPatient>>(`${environment.api_url}/patient/unslateforDeletion/${patientId}`);
  }

  getDeletedPatients(): Observable<ISibasiHTTPResponse<IPatient[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IPatient[]>>(`${environment.api_url}/patient/allDeleted`);
  }

  getPatientById(patientId: string): Observable<ISibasiHTTPResponse<IPatient>> {
    return this.httpClient.get<ISibasiHTTPResponse<IPatient>>(`${environment.api_url}/patient/${patientId}`);
  }

  getLoggedInPatient(): Observable<ISibasiHTTPResponse<IPatient>> {
    return this.httpClient.get<ISibasiHTTPResponse<IPatient>>(`${environment.api_url}/patient/loggedInPatient`);
  }

  linkPatientToUser(patientId: string, userId: string): Observable<ISibasiHTTPResponse<IPatient>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IPatient>>(`${environment.api_url}/patient/linkUser/${patientId}/${userId}`, {});
  }

  unLinkPatientToUser(patientId: string, userId: string): Observable<ISibasiHTTPResponse<IPatient>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IPatient>>(`${environment.api_url}/patient/unlinkUser/${patientId}/${userId}`, {});
  }

  getAllPatientsCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.get<ISibasiHTTPResponse<number>>(`${environment.api_url}/patient/all/count`);
  }

  getAllSearchedPatientsCount(searchParam: string): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.post<ISibasiHTTPResponse<number>>(`${environment.api_url}/patient/all/searched`, { searchParam });
  }

  getPaginatedPatients(pagination?: any): Observable<ISibasiHTTPResponse<IPatient[]>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IPatient[]>>(`${environment.api_url}/patient/all/paginated`, pagination ? pagination : {});
    // .pipe(
    //   map((res) => {
    //     const patients = res.result?.length > 0 ? res.result.slice().reverse() : [];
    //     return {
    //       ...res,
    //       result: patients
    //     };
    //   })
    // );
  }

  getRecentTenPatients(): Observable<ISibasiHTTPResponse<IPatient[]>> {
    return this.httpClient
      .get<ISibasiHTTPResponse<IPatient[]>>(`${environment.api_url}/patient/all/lastTen`).pipe(
        map((res) => {
          const patients = res.result?.length > 0 ? res.result.slice().reverse() : [];
          return {
            ...res,
            result: patients
          };
        })
      );
  }

  getPatientFilteredReports(filterParams: any): Observable<ISibasiHTTPResponse<IPatient[]>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IPatient[]>>(`${environment.api_url}/reports/patient`, filterParams);
  }
  getPatientVisitHistory(numberOfMonths: any): Observable<ISibasiHTTPResponse<any[]>> {
    return this.httpClient.post<ISibasiHTTPResponse<any[]>>(`${environment.api_url}/patient/history`, numberOfMonths);
  }
  mergePatient(mergedPatient): Observable<ISibasiHTTPResponse<IPatient>> {
    return this.httpClient.post<ISibasiHTTPResponse<IPatient>>(`${environment.api_url}/merge`, mergedPatient);
  }

  getPatientsWithoutVisitsWithRange(numberOfMonths: any): Observable<ISibasiHTTPResponse<IPatient>> {
    return this.httpClient.post<ISibasiHTTPResponse<IPatient>>(`${environment.api_url}/patient/noVisits/clinics/all`, numberOfMonths);
  }

  /**************************** VISITS **********************************/

  getAllVisits(): Observable<ISibasiHTTPResponse<IVisit[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IVisit[]>>(`${environment.api_url}/visit/all`);
  }

  getPaginatedVisits(pagination?: any): Observable<ISibasiHTTPResponse<IVisit[]>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IVisit[]>>(`${environment.api_url}/visit/all/paginated`, pagination ? pagination : {});
  }

  getAllVisitsCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.get<ISibasiHTTPResponse<number>>(`${environment.api_url}/visit/count/all`);
  }

  getVisitsFilteredReports(filterParams: any): Observable<ISibasiHTTPResponse<IVisit[]>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IVisit[]>>(`${environment.api_url}/reports/visit`, filterParams);
  }

  getPatientVisits(patientId: string): Observable<ISibasiHTTPResponse<IVisit[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IVisit[]>>(`${environment.api_url}/visit/patientVisits/${patientId}`);
  }

  updateVisit(visitData: Partial<IVisit>, visitId: string): Observable<ISibasiHTTPResponse<IVisit>> {
    return this.httpClient.post<ISibasiHTTPResponse<IVisit>>(`${environment.api_url}/visit/update/${visitId}`, visitData);
  }

  getVisitFormData(patientId: string, visitId: string): Observable<ISibasiHTTPResponse<IFormData[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IFormData[]>>(`${environment.api_url}/formData/all/${patientId}/${visitId}`);
  }
  getPatientVisitsWithFormData(patientId: string): Observable<ISibasiHTTPResponse<IFormData[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IFormData[]>>(`${environment.api_url}/patient/visits/formData/${patientId}`);
  }

  createVisit(visit: IVisit): Observable<ISibasiHTTPResponse<IVisit>> {
    return this.httpClient.post<ISibasiHTTPResponse<IVisit>>(`${environment.api_url}/visit`, visit);
  }

  getVisitDetails(patientId: string, visitId: string): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.get<ISibasiHTTPResponse<any>>(`${environment.api_url}/patient/${patientId}/visit/${visitId}`);
  }

  // deleteVisit(visitId: string): Observable<ISibasiHTTPResponse<any>> {
  //   return this.httpClient.delete<ISibasiHTTPResponse<any>>(`${environment.api_url}/visit/delete/${visitId}`);
  // }

  deleteVisit(visitId: string): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.delete<ISibasiHTTPResponse<any>>(`${environment.api_url}/visit/slateforDeletion/${visitId}`);
  }

  restoreDeletedVisit(visitId: string): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.delete<ISibasiHTTPResponse<any>>(`${environment.api_url}/visit/unslateforDeletion/${visitId}`);
  }

  getDeletedVisits(): Observable<ISibasiHTTPResponse<IVisit[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IVisit[]>>(`${environment.api_url}/visit/allDeleted`);
  }


  // getPatientHistory(patientId: string): Observable<{date: string, visits: any[]}[]> {
  //   return this.httpClient.get<ISibasiHTTPResponse<IPatient>>(`${environment.api_url}/patient/${patientId}`).pipe(
  //     map((res) => {
  //       const allHistory = res.result.visits;

  //       const allGroups = allHistory.reduce((groups, history) => {
  //         const date = history.date.split('T')[0];
  //         if (!groups[date]) {
  //           groups[date] = [];
  //         }
  //         groups[date].push(history);
  //         return groups;
  //       }, {});

  //       const groupArrays = Object.keys(allGroups).map((date) => {
  //         return {
  //           date,
  //           visits: allGroups[date]
  //         };
  //       });

  //       const sortedGroupArray = groupArrays.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  //       return sortedGroupArray;
  //     })
  //   );
  // }

  getPatientFormDataDetailsById(formDataId: string): Observable<ISibasiHTTPResponse<IFormData>> {
    return this.httpClient.get<ISibasiHTTPResponse<IFormData>>(`${environment.api_url}/formData/${formDataId}`);
  }

  dischargePatient(patientId: string): Observable<ISibasiHTTPResponse<IVisit[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IVisit[]>>(`${environment.api_url}/patient/${patientId}`);
  }

  getFormDataById(formDataId: string): Observable<ISibasiHTTPResponse<IFormData>> {
    return this.httpClient.get<ISibasiHTTPResponse<IFormData>>(`${environment.api_url}/formData/${formDataId}`);
  }

  createFormData(formData: IFormData): Observable<ISibasiHTTPResponse<IFormData>> {
    return this.httpClient.post<ISibasiHTTPResponse<IFormData>>(`${environment.api_url}/formData`, formData);
  }

  updateFormData(formData: IFormData, formDataId: string): Observable<ISibasiHTTPResponse<IFormData>> {
    return this.httpClient.post<ISibasiHTTPResponse<IFormData>>(`${environment.api_url}/formData/update/${formDataId}`, formData);
  }

  // deleteFormData(formDataId: string): Observable<ISibasiHTTPResponse<IFormData>> {
  //   return this.httpClient.delete<ISibasiHTTPResponse<IFormData>>(`${environment.api_url}/formData/delete/${formDataId}`);
  // }

  deleteFormData(formDataId: string): Observable<ISibasiHTTPResponse<IFormData>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IFormData>>(`${environment.api_url}/formData/slateforDeletion/${formDataId}`);
  }

  restoreDeletedFormData(formDataId: string): Observable<ISibasiHTTPResponse<IFormData>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IFormData>>(`${environment.api_url}/formData/unslateforDeletion/${formDataId}`);
  }

  getDeletedFormData(): Observable<ISibasiHTTPResponse<IFormData[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IFormData[]>>(`${environment.api_url}/formData/allDeleted`);
  }


  /***********************  APPOINTMENTS  ***********************/

  getAllAppointments(): Observable<ISibasiHTTPResponse<IAppointment[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IAppointment[]>>(`${environment.api_url}/appointments/all`);
  }

  getTodayAppointments(): Observable<ISibasiHTTPResponse<IAppointment[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IAppointment[]>>(`${environment.api_url}/appointments/today`);
  }

  getPatientAppointments(patientId: string): Observable<ISibasiHTTPResponse<IAppointment[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IAppointment[]>>(`${environment.api_url}/appointments/all/${patientId}`);
  }

  createAppointmentAsSysUser(appointment: IAppointment): Observable<ISibasiHTTPResponse<IAppointment>> {
    return this.httpClient.post<ISibasiHTTPResponse<IAppointment>>(`${environment.api_url}/appointments/new`, appointment);
  }

  createAppointmentAsPatient(appointment: IAppointment): Observable<ISibasiHTTPResponse<IAppointment>> {
    return this.httpClient.post<ISibasiHTTPResponse<IAppointment>>(`${environment.api_url}/appointments/new/patient`, appointment);
  }

  updateAppointment(appointment: IAppointment, appointmentId: string): Observable<ISibasiHTTPResponse<IAppointment>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IAppointment>>(`${environment.api_url}/appointments/update/${appointmentId}`, appointment);
  }

  // deleteAppointment(appointmentId: string): Observable<ISibasiHTTPResponse<IAppointment>> {
  //   return this.httpClient.delete<ISibasiHTTPResponse<IAppointment>>(`${environment.api_url}/appointments/delete/${appointmentId}`);
  // }

  deleteAppointment(appointmentId: string): Observable<ISibasiHTTPResponse<IAppointment>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IAppointment>>(`${environment.api_url}/appointments/slateforDeletion/${appointmentId}`);
  }

  restoreDeletedAppointment(appointmentId: string): Observable<ISibasiHTTPResponse<IAppointment>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IAppointment>>(`${environment.api_url}/appointments/unslateforDeletion/${appointmentId}`);
  }

  getDeletedAppointments(): Observable<ISibasiHTTPResponse<IAppointment[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IAppointment[]>>(`${environment.api_url}/appointments/allDeleted`);
  }

  getAppointmentById(appointmentId: string): Observable<ISibasiHTTPResponse<IAppointment>> {
    return this.httpClient.get<ISibasiHTTPResponse<IAppointment>>(`${environment.api_url}/appointments/${appointmentId}`);
  }

  getLoggedInPatientAppointments(): Observable<ISibasiHTTPResponse<IAppointment[]>> {
    return this.httpClient
      .get<ISibasiHTTPResponse<IAppointment[]>>(`${environment.api_url}/appointments/loggedIn`);
  }

  getAllAppointmentsCount(createdBy?): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.post<ISibasiHTTPResponse<number>>(`${environment.api_url}/appointments/all/count`, createdBy);
  }
  getAllSearchedAppointmentsCount(searchParam): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.post<ISibasiHTTPResponse<number>>(`${environment.api_url}/appointments/all/searched/count`, searchParam);
  }
  getPaginatedAppointments(pagination?: any): Observable<ISibasiHTTPResponse<IAppointment[]>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IAppointment[]>>(`${environment.api_url}/appointments/all/paginated`, pagination ? pagination : {});
  }

  rescheduleAppointment(appointmentId: string, rescheduleDate): Observable<ISibasiHTTPResponse<Partial<IAppointment>>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IAppointment>>(`${environment.api_url}/appointments/reschedule/${appointmentId}`, rescheduleDate);
  }

  approveAppointment(appointmentId: string): Observable<ISibasiHTTPResponse<IAppointment>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IAppointment>>(`${environment.api_url}/appointments/approve/${appointmentId}`, {});
  }

  disapproveAppointment(appointmentId: string): Observable<ISibasiHTTPResponse<IAppointment>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IAppointment>>(`${environment.api_url}/appointments/disapprove/${appointmentId}`, {});
  }


  // Holidays

  getAllHolidays(): Observable<ISibasiHTTPResponse<IHoliday[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IHoliday[]>>(`${environment.api_url}/holiday/all`);
  }

  createHoliday(holiday: IHoliday): Observable<ISibasiHTTPResponse<IHoliday>> {
    return this.httpClient.post<ISibasiHTTPResponse<IHoliday>>(`${environment.api_url}/holiday/new`, holiday);
  }

  updateHoliday(holiday: IHoliday, holidayId: string): Observable<ISibasiHTTPResponse<IHoliday>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IHoliday>>(`${environment.api_url}/holiday/update/${holidayId}`, holiday);
  }

  getHolidayById(holidayId: string): Observable<ISibasiHTTPResponse<IHoliday>> {
    return this.httpClient.get<ISibasiHTTPResponse<IHoliday>>(`${environment.api_url}/holiday/${holidayId}`);
  }

  deleteHoliday(holidayId: string): Observable<ISibasiHTTPResponse<IHoliday>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IHoliday>>(`${environment.api_url}/holiday/slateforDeletion/${holidayId}`);
  }

  restoreDeletedHoliday(holidayId: string): Observable<ISibasiHTTPResponse<IHoliday>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IHoliday>>(`${environment.api_url}/holiday/unslateforDeletion/${holidayId}`);
  }

  getDeletedHolidays(): Observable<ISibasiHTTPResponse<IHoliday>> {
    return this.httpClient.get<ISibasiHTTPResponse<IHoliday>>(`${environment.api_url}/holiday/allDeleted`);
  }


  /********************* LOGGED IN PATIENT VISITS ***************************/

  getLoggedInPatientVisits(): Observable<ISibasiHTTPResponse<IVisit[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IVisit[]>>(`${environment.api_url}/visit/patientVisitsForLoggedInUser`);
  }

  /********************* PATIENT ALLERGIES ***************************/

  createAllergy(allergy: IAllergy): Observable<ISibasiHTTPResponse<IAllergy>> {
    return this.httpClient.post<ISibasiHTTPResponse<IAllergy>>(`${environment.api_url}/allergy`, allergy);
  }

  getAllergies(): Observable<ISibasiHTTPResponse<IAllergy>> {
    return this.httpClient.get<ISibasiHTTPResponse<IAllergy>>(`${environment.api_url}/allergy/all`);
  }
  getAllergyById(allergyId: string): Observable<ISibasiHTTPResponse<IAllergy>> {
    return this.httpClient.get<ISibasiHTTPResponse<IAllergy>>(`${environment.api_url}/allergy/${allergyId}`);
  }
  // deleteAllergy(allergyId: string): Observable<ISibasiHTTPResponse<IAllergy>> {
  //   return this.httpClient.delete<ISibasiHTTPResponse<IAllergy>>(`${environment.api_url}/allergy/delete/${allergyId}`);
  // }

  deleteAllergy(allergyId: string): Observable<ISibasiHTTPResponse<IAllergy>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IAllergy>>(`${environment.api_url}/allergy/slateforDeletion/${allergyId}`);
  }

  restoreDeletedAllergy(allergyId: string): Observable<ISibasiHTTPResponse<IAllergy>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IAllergy>>(`${environment.api_url}/allergy/unslateforDeletion/${allergyId}`);
  }

  getDeletedAllergies(): Observable<ISibasiHTTPResponse<IAllergy[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IAllergy[]>>(`${environment.api_url}/allergy/allDeleted`);
  }

  updateAllergy(allergy: IAllergy, allergyId: string): Observable<ISibasiHTTPResponse<IAllergy>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IAllergy>>(`${environment.api_url}/allergy/update/${allergyId}`, allergy);
  }
  getPatientAllergies(patientId: string): Observable<ISibasiHTTPResponse<IAllergy>> {
    return this.httpClient.get<ISibasiHTTPResponse<IAllergy>>(`${environment.api_url}/allergy/all/${patientId}`);
  }
}
